import { format, parse } from 'date-fns';

import { ConsultInventory } from '@/rest/__generated/clearchoiceApi';

import {
	AppointmentSlot,
	AppointmentTimeSlot,
	GroupedAppointments,
	AppointmentsResponse,
} from './SchedulingWidget.types';

/**
 * Converts ClearChoice API appointment data to normalized AppointmentSlot format
 */
export function convertToAppointmentSlots(
	consultInventory: ConsultInventory[],
	_facilityId: string
): AppointmentSlot[] {
	return consultInventory
		.filter((item) => item.aptDate && item.startHour && item.startMinute)
		.map((item) => {
			const date = item.aptDate as string;
			const hour = (item.startHour as string).padStart(2, '0');
			const minute = (item.startMinute as string).padStart(2, '0');
			const time = `${hour}:${minute}`;

			return {
				date,
				time,
				consultId: item.consultId || '',
				displayTime: formatDisplayTime(time),
				displayDate: formatDisplayDate(date),
			};
		});
}

/**
 * Formats time from 24-hour format (HH:mm) to 12-hour format (h:mm A)
 */
export function formatDisplayTime(time: string): string {
	try {
		const [hours, minutes] = time.split(':').map(Number);
		const date = new Date();
		date.setHours(hours, minutes);
		return format(date, 'h:mm a');
	} catch {
		return time;
	}
}

/**
 * Formats date from yyyy-MM-dd to readable format (e.g., "Mon, Jan 27")
 */
export function formatDisplayDate(dateStr: string): string {
	try {
		const date = parse(dateStr, 'yyyy-MM-dd', new Date());
		return format(date, 'EEE, MMM d');
	} catch {
		return dateStr;
	}
}

/**
 * Groups appointment slots by date
 */
export function groupAppointmentsByDate(slots: AppointmentSlot[]): GroupedAppointments {
	return slots.reduce((acc, slot) => {
		if (!acc[slot.date]) {
			acc[slot.date] = [];
		}
		acc[slot.date].push(slot);
		return acc;
	}, {} as GroupedAppointments);
}

/**
 * Gets unique dates from grouped appointments, sorted chronologically
 */
export function getUniqueDates(grouped: GroupedAppointments): string[] {
	return Object.keys(grouped).sort((a, b) => a.localeCompare(b));
}

/**
 * Returns the first N days of appointments
 */
export function getFirstNDays(grouped: GroupedAppointments, numDays: number): GroupedAppointments {
	const dates = getUniqueDates(grouped);
	const firstDates = dates.slice(0, numDays);

	return firstDates.reduce((acc, date) => {
		acc[date] = grouped[date];
		return acc;
	}, {} as GroupedAppointments);
}

/**
 * Checks if there are more days available beyond the currently displayed ones
 */
export function hasMoreDays(grouped: GroupedAppointments, currentNumDays: number): boolean {
	const totalDays = getUniqueDates(grouped).length;
	return totalDays > currentNumDays;
}

/**
 * Reads the marketing_source cookie (set on this origin) and returns its
 * key/value pairs as URLSearchParams. Returns empty params when running
 * server-side or when the cookie is absent.
 */
export function getMarketingCookieParams(): URLSearchParams {
	if (typeof document === 'undefined') return new URLSearchParams();
	const match = document.cookie
		.split(';')
		.map((c) => c.trim())
		.find((c) => c.startsWith('marketing_source='));
	if (!match) return new URLSearchParams();
	let raw: string;
	try {
		raw = decodeURIComponent(match.slice('marketing_source='.length));
	} catch {
		return new URLSearchParams();
	}
	const clean = raw.startsWith('?') ? raw.slice(1) : raw;
	return new URLSearchParams(clean);
}

/**
 * Builds the schedule consultation URL with facility code and time slot
 */
export function buildScheduleUrl(baseUrl: string, facilityId: string, appointmentSlot?: AppointmentSlot): string {
	// globalThis.location is undefined during SSR (Node.js). Use optional chaining
	// with a fallback so relative baseUrls still resolve. In practice baseUrl is
	// always an absolute URL from Contentful, so the base is ignored by the URL
	// constructor and this path is never hit.
	const base = globalThis.location?.origin ?? 'http://ssr-placeholder.local';
	const url = new URL(baseUrl, base);
	url.searchParams.set('fc', facilityId);
	url.searchParams.set('src', 'hero');

	if (appointmentSlot) {
		url.searchParams.set('slotId', `${facilityId}-${appointmentSlot.consultId}`);
		url.searchParams.set('slotTime', appointmentSlot.consultId);
	}

	// Forward all marketing params from the marketing_source cookie.
	// schedule-consultation runs on a different origin and cannot read cookies
	// set by this domain, so we pass them via URL to preserve full attribution
	// data (cc_campaign, cc_source, gclid, etc.).
	const marketingParams = getMarketingCookieParams();
	for (const [key, value] of marketingParams) {
		if (!url.searchParams.has(key)) {
			url.searchParams.set(key, value);
		}
	}

	// Ensure trans_id is set — fall back to localStorage if the cookie did not
	// carry it or carried an empty value.
	if (!url.searchParams.get('trans_id')) {
		const storedTransId = typeof localStorage === 'undefined' ? '' : localStorage.getItem('ccTransId') ?? '';
		if (storedTransId) {
			url.searchParams.set('trans_id', storedTransId);
		}
	}

	return url.toString();
}

/**
 * Formats facility address for display
 */
export function formatFacilityAddress(facility: {
	address?: string;
	city?: string;
	state?: string;
	zipCode?: string;
}): string {
	const parts = [facility.address, facility.city, facility.state, facility.zipCode].filter(Boolean);
	return parts.join(', ');
}

/**
 * Validates zip code format (5 digits or 5+4 format)
 */
export function isValidZipCode(zipCode: string): boolean {
	// eslint-disable-next-line security/detect-unsafe-regex
	return /^\d{5}(-\d{4})?$/.test(zipCode);
}

/**
 * Extracts short facility ID from full facility code (e.g., "CC_12345" -> "12345")
 */
export function extractShortFacilityId(facilityCode: string): string {
	const parts = facilityCode.split('_');
	return parts.length > 1 ? parts[1] : facilityCode;
}

/**
 * Adds brand prefix to facility ID if not already present
 */
export function addBrandPrefix(facilityId: string, brand = 'CC'): string {
	if (facilityId.includes('_')) {
		return facilityId;
	}
	return `${brand}_${facilityId}`;
}

export interface FetchAppointmentsParams {
	facilityId: string;
	apiUrl: string;
	appName: string;
}

export type FetchAppointmentsResult = { slots: AppointmentSlot[]; error: null } | { slots: null; error: string };

function parseSlotFromEntry(slot: AppointmentTimeSlot, date: string): AppointmentSlot | null {
	const hasOpenSlots = Array.isArray(slot.openSlots) && slot.openSlots.length > 0;
	if (!hasOpenSlots) return null;

	const timeStr =
		slot.aptTime ||
		(slot.startHour && slot.startMinute
			? `${slot.startHour.padStart(2, '0')}:${slot.startMinute.padStart(2, '0')}`
			: null);

	if (!timeStr) return null;

	const datetime =
		slot.datetime ||
		`${slot.aptDate || date}T${slot.startHour?.padStart(2, '0') || '00'}:${
			slot.startMinute?.padStart(2, '0') || '00'
		}:00`;

	return {
		date: slot.aptDate || date,
		time: timeStr,
		consultId: datetime,
		displayDate: formatDisplayDate(slot.aptDate || date),
		displayTime: formatDisplayTime(timeStr),
	};
}

/**
 * Fetches and parses appointment slots for a facility from the ClearChoice API.
 * Extracted from SchedulingWidget to reduce cognitive complexity.
 */
export async function fetchAppointmentsForFacility({
	facilityId,
	apiUrl,
	appName,
}: FetchAppointmentsParams): Promise<FetchAppointmentsResult> {
	const apiCenterId = facilityId.replace(/^CC_/, '');
	const endpoint = `${apiUrl}consult-availability/${apiCenterId}?days=30&ptc=0`;
	const apiKey = globalThis.window?.__CLEARCHOICE_API_KEY__;

	const response = await fetch(endpoint, {
		method: 'GET',
		headers: {
			'Content-Type': 'application/json',
			'X-Api-Sig': appName || 'shared-components-web',
			...(apiKey ? { 'X-Api-Key': apiKey } : {}),
		},
	});

	if (!response.ok) {
		if (response.status === 404) {
			return { slots: [], error: null };
		}
		return { slots: null, error: 'Failed to load appointments. Please try again.' };
	}

	const data = (await response.json()) as AppointmentsResponse;
	const dates = data?.appointments?.dates;

	if (!dates || Object.keys(dates).length === 0) {
		return { slots: null, error: 'No appointments available' };
	}

	const slots: AppointmentSlot[] = [];

	for (const [date, dateData] of Object.entries(dates)) {
		for (const slot of dateData?.timeslots || []) {
			if (!slot) continue;
			const parsed = parseSlotFromEntry(slot, date);
			if (parsed) slots.push(parsed);
		}
	}

	if (slots.length === 0) {
		return { slots: null, error: 'No appointments available' };
	}

	return { slots, error: null };
}
